import { EntryCollection } from "@/types/entryCollection";
import { firestoreTaskPath } from "@shared/appUrls";
import { relabelReviewEvidence, reviewMetadataSha256, type RelabelReviewEvidence } from "@shared/relabelReview";
import type { Experiment } from "@/types/experiment";
import type { Task } from "@/types/task";
import type { Trial } from "@/types/trial";
import { Timestamp } from "firebase/firestore";
import { getAnnotationMetadata } from "@/utils/entryUtils";
import { generateMediaFilename, getExtensionFromPath } from "@/utils/mediaUtils";
import type { CaptureMetadata } from "@/types/annotations";
import {
  buildAnnotationClassNames,
  normalizeAnnotationClassName,
  resolveAnnotationClassName,
} from "@shared/annotationClassColors";
import type { AnnotationCategorySchema } from "@shared/annotationCategorySchemas";

const STRAWBERRY_CATEGORY_NAME = "strawberry";
const STRAWBERRY_COCO_KEYPOINT_NAMES = ["top", "bottom", "left", "right"];
const STRAWBERRY_COCO_SKELETON = [[0, 1], [2, 3]];

/**
 * Yield to the main thread to allow UI updates
 */
function yieldToMain(): Promise<void> {
  return new Promise(resolve => setTimeout(resolve, 0));
}

/**
 * COCO Format JSON Structure
 * https://cocodataset.org/#format-data
 */
interface CocoInfo {
  description: string;
  url?: string;
  version: string;
  year: number;
  contributor: string;
  date_created: string;
  gleanag_task_path?: string;
}

export interface CocoExportSource {
  organizationId: string;
  trialId: string;
  experimentId: string;
  taskId: string;
  /** Only curated and relabel tasks export internal review provenance. */
  includeReviewEvidence?: boolean;
}

interface CocoExportSnapshotContext {
  /** Current auth organization, when selection is available. */
  organizationId?: string;
  trialId?: string;
  experimentId?: string;
  taskId?: string;
  trial?: Pick<Trial, "id" | "orgId"> | null;
  experiment?: Pick<Experiment, "id" | "orgId"> | null;
  task?: Pick<Task, "id" | "metadata"> | null;
}

/**
 * Bind provenance only when every loaded document still belongs to the current route.
 * Firestore hooks retain their previous snapshot while a reused route subscribes to its
 * next document, so combining route ids with those retained objects can invent a task path.
 */
export function resolveCocoExportSourceFromSnapshots({
  organizationId,
  trialId,
  experimentId,
  taskId,
  trial,
  experiment,
  task,
}: CocoExportSnapshotContext): CocoExportSource | undefined {
  if (
    !trialId ||
    !experimentId ||
    !taskId ||
    !trial?.id ||
    !trial.orgId ||
    !experiment?.id ||
    !experiment.orgId ||
    !task?.id ||
    (organizationId !== undefined && trial.orgId !== organizationId) ||
    trial.id !== trialId ||
    experiment.id !== experimentId ||
    task.id !== taskId ||
    trial.orgId !== experiment.orgId
  ) {
    return undefined;
  }

  return {
    organizationId: experiment.orgId,
    trialId: trial.id,
    experimentId: experiment.id,
    taskId: task.id,
    ...(task.metadata?.groundTruth || task.metadata?.relabelKey ? { includeReviewEvidence: true } : {}),
  };
}

/** Whether a validated snapshot source agrees with a downstream export context. */
export function cocoExportSourceMatches(
  source: CocoExportSource | undefined,
  expected: Partial<CocoExportSource>
): source is CocoExportSource {
  return (
    !!source &&
    (expected.organizationId === undefined || source.organizationId === expected.organizationId) &&
    (expected.trialId === undefined || source.trialId === expected.trialId) &&
    (expected.experimentId === undefined || source.experimentId === expected.experimentId) &&
    (expected.taskId === undefined || source.taskId === expected.taskId)
  );
}

/** Fail closed when a downstream export context disagrees with the validated snapshot source. */
export function assertCocoExportSourceMatches(
  source: CocoExportSource | undefined,
  expected: Partial<CocoExportSource>
): CocoExportSource {
  if (!cocoExportSourceMatches(source, expected)) {
    throw new Error("Export context changed while task data was loading. Close the dialog and try again.");
  }
  return source;
}

interface CocoImage {
  gleanag_review?: RelabelReviewEvidence;
  id: number;
  file_name: string;
  width: number;
  height: number;
  date_captured?: string;
}

interface CocoCategory {
  id: number;
  name: string;
  supercategory?: string;
  keypoints?: string[];
  skeleton?: number[][];
}

interface CocoAnnotation {
  id: number;
  image_id: number;
  category_id: number;
  bbox?: [number, number, number, number];  // [x, y, width, height]
  keypoints?: number[];                      // [x1,y1,v1, x2,y2,v2, ...] flattened
  num_keypoints?: number;
  area?: number;
  iscrowd: 0 | 1;
}

interface CocoJson {
  info: CocoInfo;
  images: CocoImage[];
  categories: CocoCategory[];
  annotations: CocoAnnotation[];
}

interface NormalizedAnnotationForCoco {
  annotationKeypoints?: CocoSafeAnnotation["keypoints"];
  categoryKeypointNames?: string[];
  categoryKeypointSkeleton?: number[][];
}

/**
 * Helper to execute async tasks with concurrency limit
 */
async function executeConcurrently<T>(tasks: (() => Promise<T>)[], concurrencyLimit: number = 10): Promise<T[]> {
  const results: T[] = [];
  for (let offset = 0; offset < tasks.length; offset += concurrencyLimit) {
    // Settle each bounded batch before propagating errors. Preserve the source order.
    const batch = await Promise.allSettled(tasks.slice(offset, offset + concurrencyLimit).map((task) => task()));
    for (const result of batch) {
      if (result.status === "rejected") throw result.reason;
      results.push(result.value);
    }
  }
  return results;
}

/**
 * Entry with its metadata for processing
 */
interface EntryWithMetadata {
  review?: RelabelReviewEvidence;
  plotId: string;
  entryType: string;
  sampleKey: string;
  createdAt: Timestamp;
  imgCloudPath: string;
  collectionId: string;
  entryConfigId?: string;
  metadata: CocoSafeCaptureMetadata;
}

/** The subset of an annotation that this exporter reads or serializes. */
interface CocoSafeAnnotation {
  category: {
    name: string;
    supercategory?: string;
    keypointNames?: string[];
    keypointSkeleton?: number[][];
  };
  bbox?: { x: number; y: number; width: number; height: number };
  keypoints?: Array<{ name?: string; x: number; y: number; visibility: number }>;
  attributes?: Record<string, unknown>;
  userAdjustment?: {
    categoryChange?: unknown;
  };
  userVisibility: { isVisible: boolean };
}

/** The subset of a capture sidecar that this exporter reads or serializes. */
interface CocoSafeCaptureMetadata {
  createdAt?: string;
  image: { width: number; height: number };
  annotations: {
    annotations: CocoSafeAnnotation[];
    summary?: unknown;
  };
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function isFiniteNumber(value: unknown): value is number {
  return typeof value === "number" && Number.isFinite(value);
}

function isOptionalStringArray(value: unknown): value is string[] | undefined {
  return value === undefined || (Array.isArray(value) && value.every((item) => typeof item === "string"));
}

function hasValidKeypointNames(value: string[] | undefined): boolean {
  if (value === undefined) return true;
  if (value.length === 0) return false;
  const normalized = value.map((name) => name.trim());
  return normalized.every(Boolean) && new Set(normalized).size === normalized.length;
}

function isOptionalSkeleton(value: unknown, keypointNames: string[] | undefined): boolean {
  if (value === undefined) return true;
  if (!Array.isArray(value)) return false;
  if (value.length > 0 && !keypointNames) return false;
  return value.every(
    (pair) =>
      Array.isArray(pair) &&
      pair.length === 2 &&
      pair.every(
        (index) =>
          Number.isInteger(index) &&
          index >= 0 &&
          index < (keypointNames?.length ?? 0)
      ) &&
      pair[0] !== pair[1]
  );
}

function getCocoAnnotationValidationError(value: unknown): string | undefined {
  if (!isRecord(value) || !isRecord(value.category) || !isRecord(value.userVisibility)) {
    return "annotation, category, and userVisibility must be objects";
  }
  const keypointNames = value.category.keypointNames;
  if (
    typeof value.category.name !== "string" ||
    normalizeAnnotationClassName(value.category.name).length === 0
  ) {
    return "category name must be a non-empty string";
  }
  if (value.category.supercategory !== undefined && typeof value.category.supercategory !== "string") {
    return "category supercategory must be a string";
  }
  if (typeof value.userVisibility.isVisible !== "boolean") {
    return "userVisibility.isVisible must be a boolean";
  }
  if (!isOptionalStringArray(keypointNames)) {
    return "category keypointNames must be an array of strings";
  }
  if (!hasValidKeypointNames(keypointNames)) {
    return "category keypointNames must be non-empty, non-blank, and unique";
  }
  if (!isOptionalSkeleton(value.category.keypointSkeleton, keypointNames)) {
    return "category keypointSkeleton must contain valid two-index edges";
  }

  if (value.bbox !== undefined) {
    if (
      !isRecord(value.bbox) ||
      !isFiniteNumber(value.bbox.x) ||
      !isFiniteNumber(value.bbox.y) ||
      !isFiniteNumber(value.bbox.width) ||
      !isFiniteNumber(value.bbox.height) ||
      value.bbox.width <= 0 ||
      value.bbox.height <= 0
    ) {
      return "bounding box coordinates must be finite with positive width and height";
    }
  }

  if (value.keypoints !== undefined) {
    if (
      !Array.isArray(value.keypoints) ||
      !value.keypoints.every(
        (keypoint) =>
          isRecord(keypoint) &&
          isFiniteNumber(keypoint.x) &&
          isFiniteNumber(keypoint.y) &&
          (keypoint.visibility === 0 || keypoint.visibility === 1 || keypoint.visibility === 2) &&
          (keypoint.name === undefined || typeof keypoint.name === "string")
      )
    ) {
      return "keypoints must have finite coordinates and COCO visibility 0, 1, or 2";
    }

    if (value.keypoints.length > 0) {
      const safeAnnotation = value as unknown as CocoSafeAnnotation;
      const normalized = normalizeAnnotationForCoco({
        ...safeAnnotation,
        category: {
          ...safeAnnotation.category,
          name: normalizeAnnotationClassName(safeAnnotation.category.name),
        },
      });
      const normalizedNames = normalized.categoryKeypointNames ?? keypointNames;
      const normalizedKeypoints = normalized.annotationKeypoints ?? [];
      if (!normalizedNames || normalizedKeypoints.length !== normalizedNames.length) {
        return "keypoints must exactly match the category keypoint schema length";
      }
      if (normalizedKeypoints.some((keypoint, index) => keypoint.name !== normalizedNames[index])) {
        return "keypoints must exactly match the category keypoint schema order and names";
      }
    }
  }

  // `numKeypoints` is a legacy denormalized field. Several older writers counted
  // stored visibility-0 placeholders, so it is not trustworthy validation input.
  // COCO `num_keypoints` is derived from the validated keypoint array below.
  return undefined;
}

function isExplicitlyHiddenAnnotation(value: unknown): boolean {
  return (
    isRecord(value) &&
    isRecord(value.userVisibility) &&
    value.userVisibility.isVisible === false
  );
}

function parseCompletedAnnotationResult(
  metadata: unknown,
  captureMetadataCloudPath: string
): CocoSafeCaptureMetadata | null {
  if (!isRecord(metadata)) {
    console.warn(
      `[COCO export] ${captureMetadataCloudPath}: skipping malformed sidecar; ` +
      "metadata must be an object."
    );
    return null;
  }
  // Capture sidecars exist for ordinary photo and scalar entries too. The
  // absence of annotations means the sidecar is outside COCO's scope, not bad data.
  if (metadata.annotations === undefined) return null;
  if (!isRecord(metadata.image) || !isRecord(metadata.annotations)) {
    console.warn(
      `[COCO export] ${captureMetadataCloudPath}: skipping malformed sidecar; ` +
      "image and present annotations must be objects."
    );
    return null;
  }
  if (
    !isFiniteNumber(metadata.image.width) ||
    metadata.image.width <= 0 ||
    !isFiniteNumber(metadata.image.height) ||
    metadata.image.height <= 0 ||
    (metadata.createdAt !== undefined && typeof metadata.createdAt !== "string") ||
    !Array.isArray(metadata.annotations.annotations)
  ) {
    console.warn(
      `[COCO export] ${captureMetadataCloudPath}: skipping malformed sidecar; ` +
      "image dimensions, createdAt, or annotations array is invalid."
    );
    return null;
  }

  // Deferred captures are initially uploaded with an empty annotations array
  // and no summary. A completed zero-detection result adds a zero-valued
  // summary, while older completed results may only contain annotations.
  const rawAnnotations = metadata.annotations.annotations;
  const summary = metadata.annotations.summary;
  if (rawAnnotations.length === 0) {
    // Empty annotations are safe training negatives only when the producer
    // explicitly attests a completed zero-object result. Merely having an
    // object-shaped summary (including `{}`) is not completion evidence.
    if (
      !isRecord(summary) ||
      summary.totalObjects !== 0 ||
      summary.visibleObjects !== 0
    ) {
      return null;
    }

    return {
      createdAt: metadata.createdAt,
      image: {
        width: metadata.image.width,
        height: metadata.image.height,
      },
      annotations: { annotations: [], summary },
    };
  }

  const annotations: CocoSafeAnnotation[] = [];
  let skippedPotentiallyVisibleAnnotation = false;
  rawAnnotations.forEach((annotation, index) => {
    const error = getCocoAnnotationValidationError(annotation);
    if (error) {
      console.warn(
        `[COCO export] ${captureMetadataCloudPath}: skipping annotation ${index + 1}; ${error}.`
      );
      if (!isExplicitlyHiddenAnnotation(annotation)) {
        skippedPotentiallyVisibleAnnotation = true;
      }
      return;
    }
    const safeAnnotation = annotation as CocoSafeAnnotation;
    annotations.push({
      ...safeAnnotation,
      category: {
        ...safeAnnotation.category,
        name: normalizeAnnotationClassName(safeAnnotation.category.name),
      },
    });
  });

  const hasValidVisibleAnnotation = annotations.some(
    (annotation) => annotation.userVisibility.isVisible
  );
  if (skippedPotentiallyVisibleAnnotation && !hasValidVisibleAnnotation) {
    console.warn(
      `[COCO export] ${captureMetadataCloudPath}: skipping image because no valid visible ` +
      "annotation remains; exporting it would create a false training negative."
    );
    return null;
  }

  return {
    createdAt: metadata.createdAt,
    image: {
      width: metadata.image.width,
      height: metadata.image.height,
    },
    annotations: { annotations, summary },
  };
}

/**
 * Collect all entries that have both imgCloudPath and captureMetadataCloudPath
 */
function collectEntriesWithAnnotations(entryCollections: EntryCollection[]): Array<{
  review: RelabelReviewEvidence;
  plotId: string;
  entryType: string;
  sampleKey: string;
  createdAt: Timestamp;
  imgCloudPath: string;
  captureMetadataCloudPath: string;
  collectionId: string;
  entryConfigId?: string;
}> {
  const entries: Array<{
    review: RelabelReviewEvidence;
    plotId: string;
    entryType: string;
    sampleKey: string;
    createdAt: Timestamp;
    imgCloudPath: string;
    captureMetadataCloudPath: string;
    collectionId: string;
    entryConfigId?: string;
  }> = [];

  for (const collection of entryCollections) {
    const plotRef = collection.plotRef;
    const plotId = typeof plotRef === "string" ? plotRef : plotRef.id;
    const collectionId = collection.id || "unknown";
    const entryConfigId =
      typeof collection.entryConfigRef === "string"
        ? collection.entryConfigRef.split("/").filter(Boolean).at(-1)
        : collection.entryConfigRef?.id;

    // Helper to extract entries from a map
    // NOTE: createdAt is on the EntryCollection, not on individual entries!
    const extractFromMap = <T extends { imgCloudPath?: string; captureMetadataCloudPath?: string }>(
      entriesMap: Record<string, T> | undefined,
      entryType: string
    ) => {
      if (!entriesMap) return;

      // Get createdAt from the EntryCollection (not from individual entries)
      const collectionCreatedAt = collection.createdAt as Timestamp;
      if (!collectionCreatedAt) return;

      for (const [sampleKey, entry] of Object.entries(entriesMap)) {
        if (entry.imgCloudPath && entry.captureMetadataCloudPath) {
          entries.push({
            review: relabelReviewEvidence(entry, collectionId, entryType, sampleKey),
            plotId,
            entryType,
            sampleKey,
            createdAt: collectionCreatedAt, // Use collection's createdAt
            imgCloudPath: entry.imgCloudPath,
            captureMetadataCloudPath: entry.captureMetadataCloudPath,
            collectionId,
            entryConfigId,
          });
        }
      }
    };

    // Process all entry types
    switch (collection.type) {
      case "text":
        extractFromMap(collection.textEntries, "text");
        break;
      case "number":
        extractFromMap(collection.numberEntries, "number");
        break;
      case "category":
        extractFromMap(collection.categoryEntries, "category");
        break;
      case "date":
        extractFromMap(collection.dateEntries, "date");
        break;
      case "boolean":
        extractFromMap(collection.booleanEntries, "boolean");
        break;
      case "percentage":
        extractFromMap(collection.percentageEntries, "percentage");
        break;
      case "photo":
        extractFromMap(collection.photoEntries, "photo");
        break;
      case "transcription":
        extractFromMap(collection.transcriptionEntries, "transcription");
        break;
      case "leafAreaIndex":
        extractFromMap(collection.leafAreaIndexEntries, "leafAreaIndex");
        break;
      case "ruler":
        extractFromMap(collection.rulerEntries, "ruler");
        break;
      case "imageMeasurement":
        extractFromMap(collection.imageMeasurementEntries, "imageMeasurement");
        break;
      case "standCountMeasurement":
        extractFromMap(collection.standCountMeasurementEntries, "standCountMeasurement");
        break;
    }
  }

  return entries;
}

/**
 * Build category map and assign IDs
 */
function buildCategoryMap(
  entriesWithMetadata: EntryWithMetadata[],
  configuredCategories: readonly (string | AnnotationCategorySchema)[],
  classNamesByEntryConfigId?: ReadonlyMap<string, readonly string[]>
): Map<string, CocoCategory> {
  const configuredSchemas = configuredCategories.map((category) =>
    typeof category === "string" ? { name: category } : category
  );
  const configuredNames = configuredSchemas.map(({ name }) => name);
  const resolvedAnnotations = entriesWithMetadata.flatMap((entry) => {
    const entryClassNames =
      (entry.entryConfigId ? classNamesByEntryConfigId?.get(entry.entryConfigId) : undefined) ??
      configuredNames;
    return (entry.metadata.annotations?.annotations ?? []).map((annotation) => ({
      annotation,
      categoryName: resolveAnnotationClassName(annotation, entryClassNames),
    }));
  });
  const categoryNames = buildAnnotationClassNames(
    configuredNames,
    resolvedAnnotations.map(({ categoryName }) => categoryName)
  );
  const configuredByName = new Map(
    configuredSchemas.map((schema) => [normalizeAnnotationClassName(schema.name), schema])
  );
  const categoryMap = new Map<string, CocoCategory>(
    categoryNames.map((name, index) => {
      const configured = configuredByName.get(name);
      return [
        name,
        {
          id: index + 1,
          name,
          ...(configured?.supercategory ? { supercategory: configured.supercategory } : {}),
          ...(configured?.keypointNames ? { keypoints: [...configured.keypointNames] } : {}),
          ...(configured?.keypointSkeleton
            ? { skeleton: configured.keypointSkeleton.map((pair) => [...pair]) }
            : {}),
        },
      ];
    })
  );
  const persistedSchemasByName = new Map<
    string,
    Map<string, Pick<NormalizedAnnotationForCoco, "categoryKeypointNames" | "categoryKeypointSkeleton">>
  >();

  for (const { annotation, categoryName } of resolvedAnnotations) {
    const normalizedAnnotation = normalizeAnnotationForCoco(annotation);
    const existingCategory = categoryMap.get(categoryName);
    if (!existingCategory) continue;

    if (!existingCategory.supercategory && annotation.category.supercategory) {
      existingCategory.supercategory = annotation.category.supercategory;
    }
    if (normalizedAnnotation.categoryKeypointNames) {
      const signature = keypointSchemaSignature(
        normalizedAnnotation.categoryKeypointNames,
        normalizedAnnotation.categoryKeypointSkeleton
      );
      const schemas = persistedSchemasByName.get(categoryName) ?? new Map();
      if (!schemas.has(signature)) {
        schemas.set(signature, {
          categoryKeypointNames: normalizedAnnotation.categoryKeypointNames,
          categoryKeypointSkeleton: normalizedAnnotation.categoryKeypointSkeleton,
        });
      }
      persistedSchemasByName.set(categoryName, schemas);
    }
  }

  for (const [categoryName, schemas] of persistedSchemasByName) {
    const existingCategory = categoryMap.get(categoryName);
    if (!existingCategory) continue;

    let selectedSchema = schemas.values().next().value;
    if (schemas.size > 1) {
      const configured = configuredByName.get(categoryName);
      const configuredSignature = configured?.keypointNames
        ? keypointSchemaSignature(configured.keypointNames, configured.keypointSkeleton)
        : undefined;
      if (configuredSignature && schemas.has(configuredSignature)) {
        selectedSchema = schemas.get(configuredSignature);
      }
      console.warn(
        `[COCO export] conflicting persisted keypoint schemas for annotation category ` +
          `"${categoryName}"; using ${configuredSignature && schemas.has(configuredSignature) ? "configured" : "first persisted"} schema and skipping incompatible annotations.`
      );
    }

    if (!selectedSchema?.categoryKeypointNames) continue;
    existingCategory.keypoints = [...selectedSchema.categoryKeypointNames];
    if (selectedSchema.categoryKeypointSkeleton) {
      existingCategory.skeleton = selectedSchema.categoryKeypointSkeleton.map((pair) => [...pair]);
    } else {
      delete existingCategory.skeleton;
    }
  }

  return categoryMap;
}

function sortKeypointsByPosition(
  keypoints: NonNullable<CocoSafeAnnotation["keypoints"]>,
  primaryAxis: "x" | "y",
  secondaryAxis: "x" | "y"
): NonNullable<CocoSafeAnnotation["keypoints"]> {
  return [...keypoints].sort((a, b) => {
    if (a[primaryAxis] !== b[primaryAxis]) {
      return a[primaryAxis] - b[primaryAxis];
    }

    if (a[secondaryAxis] !== b[secondaryAxis]) {
      return a[secondaryAxis] - b[secondaryAxis];
    }

    return (a.name ?? "").localeCompare(b.name ?? "");
  });
}

function normalizeStrawberryMeasurementKeypoints(
  annotation: CocoSafeAnnotation
): NormalizedAnnotationForCoco | null {
  if (annotation.category.name !== STRAWBERRY_CATEGORY_NAME) {
    return null;
  }

  const semanticSchema = {
    categoryKeypointNames: [...STRAWBERRY_COCO_KEYPOINT_NAMES],
    categoryKeypointSkeleton: STRAWBERRY_COCO_SKELETON.map((pair) => [...pair]),
  };
  if (!annotation.keypoints?.length) {
    return {
      annotationKeypoints: annotation.keypoints,
      ...semanticSchema,
    };
  }

  const lengthKeypoints = annotation.keypoints.filter(
    (kp) => kp.name === "length_start" || kp.name === "length_end" || kp.name === "top" || kp.name === "bottom"
  );
  const widthKeypoints = annotation.keypoints.filter(
    (kp) => kp.name === "width_start" || kp.name === "width_end" || kp.name === "left" || kp.name === "right"
  );

  const invisible = (name: string) => ({ name, x: 0, y: 0, visibility: 0 });
  const resolveAxis = (
    keypoints: NonNullable<CocoSafeAnnotation["keypoints"]>,
    firstName: string,
    secondName: string,
    primaryAxis: "x" | "y",
    secondaryAxis: "x" | "y"
  ) => {
    if (keypoints.length >= 2) {
      const [first, second] = sortKeypointsByPosition(keypoints, primaryAxis, secondaryAxis);
      return [{ ...first, name: firstName }, { ...second, name: secondName }];
    }
    const only = keypoints[0];
    if (only?.name === firstName) return [{ ...only, name: firstName }, invisible(secondName)];
    if (only?.name === secondName) return [invisible(firstName), { ...only, name: secondName }];
    return [invisible(firstName), invisible(secondName)];
  };

  const [top, bottom] = resolveAxis(lengthKeypoints, "top", "bottom", "y", "x");
  const [left, right] = resolveAxis(widthKeypoints, "left", "right", "x", "y");

  return {
    annotationKeypoints: [
      { ...top, name: "top" },
      { ...bottom, name: "bottom" },
      { ...left, name: "left" },
      { ...right, name: "right" },
    ],
    ...semanticSchema,
  };
}

function normalizeAnnotationForCoco(annotation: CocoSafeAnnotation): NormalizedAnnotationForCoco {
  const strawberryMeasurementKeypoints = normalizeStrawberryMeasurementKeypoints(annotation);
  if (strawberryMeasurementKeypoints) {
    return strawberryMeasurementKeypoints;
  }

  return {
    annotationKeypoints: annotation.keypoints,
    categoryKeypointNames: annotation.category.keypointNames,
    categoryKeypointSkeleton: annotation.category.keypointSkeleton,
  };
}

function keypointSchemaSignature(
  keypointNames: readonly string[],
  keypointSkeleton?: readonly (readonly number[])[]
): string {
  return JSON.stringify({
    keypointNames,
    keypointSkeleton: keypointSkeleton ?? null,
  });
}

function annotationMatchesCategoryKeypointSchema(
  normalizedAnnotation: NormalizedAnnotationForCoco,
  category: CocoCategory
): boolean {
  if (!normalizedAnnotation.annotationKeypoints?.length) return true;
  if (!normalizedAnnotation.categoryKeypointNames || !category.keypoints) return false;
  return (
    keypointSchemaSignature(
      normalizedAnnotation.categoryKeypointNames,
      normalizedAnnotation.categoryKeypointSkeleton
    ) === keypointSchemaSignature(category.keypoints, category.skeleton)
  );
}

/**
 * Convert annotation bbox to COCO format
 */
function bboxToCoco(bbox: { x: number; y: number; width: number; height: number }): [number, number, number, number] {
  return [bbox.x, bbox.y, bbox.width, bbox.height];
}

/**
 * Convert annotation keypoints to COCO format (flattened array)
 */
function keypointsToCoco(keypoints?: Array<{ x: number; y: number; visibility: number }>): number[] | undefined {
  if (!keypoints || keypoints.length === 0) return undefined;

  const flattened: number[] = [];
  for (const kp of keypoints) {
    flattened.push(kp.x, kp.y, kp.visibility);
  }
  return flattened;
}

/**
 * Calculate bbox area
 */
function calculateBboxArea(bbox?: { x: number; y: number; width: number; height: number }): number {
  if (!bbox) return 0;
  return bbox.width * bbox.height;
}

/**
 * Generate COCO annotations JSON for download.
 *
 * Processes all entry collections to find entries with annotation metadata,
 * fetches the metadata concurrently, filters annotations to visible ones, and
 * generates a COCO-format JSON file. Completed images remain in the export when
 * all annotations were hidden or the completed result found zero objects, so
 * reviewed negatives are retained without treating pending placeholders as negatives.
 * Category ids follow configured task category order, then deterministic persisted-only
 * names. Closed-set config schemas provide a fallback for negative-only exports, while
 * a coherent schema from completed positive annotations remains authoritative. If
 * persisted schemas conflict, a matching configured schema wins; otherwise the first
 * persisted schema wins, and annotations incompatible with that choice are skipped.
 *
 * Returns null if no completed annotation results are found.
 *
 * @param entryCollections - All entry collections for the task
 * @param taskName - Task name for metadata
 * @param shouldCancel - Optional callback to check if operation should be cancelled
 * @param configuredCategories - Stable task-level categories and fallback schemas from trait configs
 * @returns COCO JSON string, or null if no completed annotation results exist
 */
export async function generateCocoAnnotations(
  entryCollections: EntryCollection[],
  taskName: string,
  shouldCancel?: () => boolean,
  metadataByCloudPath?: Map<string, CaptureMetadata | null>,
  source?: CocoExportSource,
  configuredCategories: readonly (string | AnnotationCategorySchema)[] = [],
  classNamesByEntryConfigId?: ReadonlyMap<string, readonly string[]>
): Promise<string | null> {
  // Step 1: Collect all entries with annotation metadata
  const entries = collectEntriesWithAnnotations(entryCollections);

  if (entries.length === 0) {
    return null;
  }

  // Step 2: Resolve metadata for each entry (use cache if available, otherwise fetch)
  const toEntryWithMetadata = async (
    entry: typeof entries[0], metadata: CocoSafeCaptureMetadata, originalMetadata: unknown
  ): Promise<EntryWithMetadata> => ({
    ...(source?.includeReviewEvidence ? {
      review: { ...entry.review, annotationMetadataSha256: await reviewMetadataSha256(originalMetadata) },
    } : {}),
    plotId: entry.plotId,
    entryType: entry.entryType,
    sampleKey: entry.sampleKey,
    createdAt: entry.createdAt,
    imgCloudPath: entry.imgCloudPath,
    collectionId: entry.collectionId,
    entryConfigId: entry.entryConfigId,
    metadata,
  });

  let entriesWithMetadata: EntryWithMetadata[];

  if (metadataByCloudPath) {
    entriesWithMetadata = (await executeConcurrently(entries
      .map((entry) => async () => {
        const metadata = metadataByCloudPath.get(entry.captureMetadataCloudPath);
        if (!metadata) return null;
        const completedMetadata = parseCompletedAnnotationResult(
          metadata,
          entry.captureMetadataCloudPath
        );
        if (!completedMetadata) return null;
        return toEntryWithMetadata(entry, completedMetadata, metadata);
      }), 10))
      .filter((r): r is EntryWithMetadata => r !== null);
  } else {
    const fetchTasks = entries.map((entry) => {
      return async (): Promise<EntryWithMetadata | null> => {
        const metadata = await getAnnotationMetadata(entry.captureMetadataCloudPath);
        if (!metadata) return null;
        const completedMetadata = parseCompletedAnnotationResult(
          metadata,
          entry.captureMetadataCloudPath
        );
        if (!completedMetadata) return null;
        return toEntryWithMetadata(entry, completedMetadata, metadata);
      };
    });

    const fetchedResults = await executeConcurrently(fetchTasks, 10);
    entriesWithMetadata = fetchedResults.filter((r): r is EntryWithMetadata => r !== null);
  }

  if (entriesWithMetadata.length === 0) {
    return null;
  }

  // Step 3: Build category map
  const categoryMap = buildCategoryMap(
    entriesWithMetadata,
    configuredCategories,
    classNamesByEntryConfigId
  );

  // Step 4: Build COCO structure
  const coco: CocoJson = {
    info: {
      description: `Annotations for task: ${taskName}`,
      version: "1.0",
      year: new Date().getFullYear(),
      contributor: "GleanAg",
      date_created: new Date().toISOString(),
      ...(source
        ? {
            gleanag_task_path: firestoreTaskPath(
              source.organizationId,
              source.trialId,
              source.experimentId,
              source.taskId
            ),
          }
        : {}),
    },
    images: [],
    categories: Array.from(categoryMap.values()),
    annotations: [],
  };

  let imageIdCounter = 1;
  let annotationIdCounter = 1;

  // Step 5: Process each entry and its annotations
  for (const entry of entriesWithMetadata) {
    if (shouldCancel?.()) {
      throw new Error("Download cancelled");
    }

    const { metadata, plotId, entryType, sampleKey, createdAt, imgCloudPath, collectionId, entryConfigId } = entry;
    const entryClassNames =
      (entryConfigId ? classNamesByEntryConfigId?.get(entryConfigId) : undefined) ??
      configuredCategories.map((category) =>
        typeof category === "string" ? category : category.name
      );

    // Generate filename using same logic as media downloads
    const extension = getExtensionFromPath(imgCloudPath);
    const filename = generateMediaFilename(plotId, entryType, sampleKey, createdAt, extension, collectionId);

    // Get visible annotations for this image
    const visibleAnnotations = (metadata.annotations?.annotations || []).filter(
      (ann) => ann.userVisibility.isVisible
    );
    const compatibleVisibleAnnotations = visibleAnnotations.filter((annotation, index) => {
      const categoryName = resolveAnnotationClassName(annotation, entryClassNames);
      const category = categoryMap.get(categoryName);
      const normalizedAnnotation = normalizeAnnotationForCoco(annotation);
      if (category && annotationMatchesCategoryKeypointSchema(normalizedAnnotation, category)) {
        return true;
      }
      console.warn(
        `[COCO export] ${imgCloudPath}: skipping annotation ${index + 1}; ` +
          `its keypoint schema conflicts with category "${categoryName}".`
      );
      return false;
    });

    if (visibleAnnotations.length > 0 && compatibleVisibleAnnotations.length === 0) {
      console.warn(
        `[COCO export] ${imgCloudPath}: skipping image because no compatible visible ` +
          "annotation remains; exporting it would create a false training negative."
      );
      continue;
    }

    // Add image to COCO
    const imageId = imageIdCounter++;
    const cocoImage: CocoImage = {
      ...(entry.review ? { gleanag_review: entry.review } : {}),
      id: imageId,
      file_name: filename,
      width: metadata.image.width,
      height: metadata.image.height,
      date_captured: metadata.createdAt,
    };
    coco.images.push(cocoImage);

    // Add all visible annotations for this image
    for (const annotation of compatibleVisibleAnnotations) {
      const categoryName = resolveAnnotationClassName(annotation, entryClassNames);
      const category = categoryMap.get(categoryName);
      if (!category) continue;
      const normalizedAnnotation = normalizeAnnotationForCoco(annotation);

      const cocoAnnotation: CocoAnnotation = {
        id: annotationIdCounter++,
        image_id: imageId,
        category_id: category.id,
        iscrowd: 0,
      };

      // Add bbox if present
      if (annotation.bbox) {
        cocoAnnotation.bbox = bboxToCoco(annotation.bbox);
        cocoAnnotation.area = calculateBboxArea(annotation.bbox);
      }

      // Add keypoints if present
      if (normalizedAnnotation.annotationKeypoints && normalizedAnnotation.annotationKeypoints.length > 0) {
        cocoAnnotation.keypoints = keypointsToCoco(normalizedAnnotation.annotationKeypoints);
        cocoAnnotation.num_keypoints = normalizedAnnotation.annotationKeypoints.filter(
          ({ visibility }) => visibility > 0
        ).length;
      }

      coco.annotations.push(cocoAnnotation);
    }
  }

  // No metadata-backed images means there is no useful COCO artifact. An
  // annotation-free result is still useful: it represents reviewed negatives.
  if (coco.images.length === 0) {
    return null;
  }

  // Return formatted JSON
  return JSON.stringify(coco, null, 2);
}
